"use client";

import React, {
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  Row,
  Col,
  Card,
  Form,
  Button,
  Spinner,
  Badge,
} from "react-bootstrap";
import {
  useNavigate,
  useParams,
} from "react-router-dom";
import api from "../../services/api";
import Swal from "sweetalert2";
import StoreProductCard from "./StoreProductCard";

const StoreProducts = () => {
  const { storeCode } = useParams();
  const navigate = useNavigate();

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  const [cart, setCart] = useState(() => {
    try {
      return JSON.parse(
        localStorage.getItem(
          "store_cart"
        ) || "{}"
      );
    } catch {
      return {};
    }
  });

  useEffect(() => {
    localStorage.setItem(
      "store_cart",
      JSON.stringify(cart)
    );
  }, [cart]);

  useEffect(() => {
    const fetchProducts = async () => {
      setLoading(true);

      try {
        const { data } = await api.get(
          "/store-products",
          {
            params: {
              storeCode,
            },
          }
        );

        const items =
          Array.isArray(data)
            ? data
            : data.items || [];

        setProducts(items);
      } catch (err) {
        Swal.fire({
          icon: "error",
          title: "خطا",
          text:
            "خطا در دریافت محصولات فروشگاه",
          confirmButtonText: "باشه",
        });
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, [storeCode]);

  const filteredProducts =
    useMemo(() => {
      if (!search.trim()) {
        return products;
      }

      const query =
        search.toLowerCase();

      return products.filter((product) => {
        const name =
          product.name ||
          product.product?.title ||
          "";

        const description =
          product.description || "";

        return (
          name
            .toLowerCase()
            .includes(query) ||
          description
            .toLowerCase()
            .includes(query)
        );
      });
    }, [products, search]);

  const getCartKey = (product) =>
    product.code ||
    product.storeProductCode ||
    product.id;

  const getQuantity = (product) => {
    const key = getCartKey(product);

    return (
      cart[storeCode]?.items?.[key]
        ?.quantity || 0
    );
  };

  const addToCart = (product) => {
    const key = getCartKey(product);

    setCart((prev) => {
      const storeCart =
        prev[storeCode] || {
          storeCode,
          items: {},
        };

      const current =
        storeCart.items[key] || {
          product,
          quantity: 0,
        };

      return {
        ...prev,
        [storeCode]: {
          ...storeCart,
          items: {
            ...storeCart.items,
            [key]: {
              product,
              quantity:
                current.quantity + 1,
            },
          },
        },
      };
    });
  };

  const removeFromCart = (product) => {
    const key = getCartKey(product);

    setCart((prev) => {
      const storeCart =
        prev[storeCode];

      if (!storeCart) {
        return prev;
      }

      const current =
        storeCart.items[key];

      if (!current) {
        return prev;
      }

      const items = {
        ...storeCart.items,
      };

      if (current.quantity <= 1) {
        delete items[key];
      } else {
        items[key] = {
          ...current,
          quantity:
            current.quantity - 1,
        };
      }

      return {
        ...prev,
        [storeCode]: {
          ...storeCart,
          items,
        },
      };
    });
  };

  const cartCount =
    Object.values(
      cart[storeCode]?.items || {}
    ).reduce(
      (sum, item) =>
        sum + Number(item.quantity || 0),
      0
    );

  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" />
      </div>
    );
  }

  return (
    <>
      {/* Header */}
      <Card className="border-0 rounded-3 mb-4">
        <Card.Body className="p-4">
          <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
            <div>
              <Button
                variant="link"
                className="p-0 mb-2"
                onClick={() =>
                  navigate("/user/stores")
                }
              >
                ← بازگشت به فروشگاه‌ها
              </Button>

              <h3 className="mb-1">
                محصولات فروشگاه
              </h3>

              <span className="text-secondary">
                {products.length} محصول
              </span>
            </div>

            <Button
              variant="primary"
              onClick={() =>
                navigate(
                  `/user/stores/${storeCode}/cart`
                )
              }
              disabled={cartCount === 0}
            >
              <span className="material-symbols-outlined align-middle me-1">
                shopping_cart
              </span>

              سبد خرید

              {cartCount > 0 && (
                <Badge
                  bg="light"
                  text="primary"
                  className="ms-2"
                >
                  {cartCount}
                </Badge>
              )}
            </Button>
          </div>
        </Card.Body>
      </Card>

      {/* Search */}
      <Card className="border-0 rounded-3 mb-4">
        <Card.Body className="p-4">
          <Form.Control
            value={search}
            onChange={(e) =>
              setSearch(e.target.value)
            }
            placeholder="جستجو در محصولات..."
            className="rounded-3"
          />
        </Card.Body>
      </Card>

      {/* Products */}
      <Row className="g-4">
        {filteredProducts.length === 0 ? (
          <Col xs={12}>
            <Card className="border-0 rounded-3">
              <Card.Body className="text-center p-5">
                محصولی پیدا نشد.
              </Card.Body>
            </Card>
          </Col>
        ) : (
          filteredProducts.map(
            (product) => (
              <Col
                xs={12}
                sm={6}
                lg={4}
                xl={3}
                key={getCartKey(product)}
              >
                <StoreProductCard
                  product={product}
                  quantity={getQuantity(
                    product
                  )}
                  onAdd={addToCart}
                  onRemove={
                    removeFromCart
                  }
                />
              </Col>
            )
          )
        )}
      </Row>
    </>
  );
};

export default StoreProducts;